
import { useEffect, useRef, useState, useCallback } from 'react';

const ScrollingTradeRoutes = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const rafRef = useRef<number>(0);

  const updateScroll = useCallback(() => {
    const scrollY = window.scrollY;
    const heroH = window.innerHeight;
    const docH = document.documentElement.scrollHeight - window.innerHeight;

    // Show after scrolling past 60% of hero
    const fadeStart = heroH * 0.5;
    const fadeEnd = heroH * 0.8;
    const vis = Math.min(Math.max((scrollY - fadeStart) / (fadeEnd - fadeStart), 0), 1);
    setIsVisible(vis > 0.01);

    // Progress: 0 at hero bottom, 1 at page bottom
    const contentStart = heroH;
    const progress = Math.min(Math.max((scrollY - contentStart * 0.5) / (docH - contentStart * 0.3), 0), 1);
    setScrollProgress(progress);

    rafRef.current = requestAnimationFrame(updateScroll);
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(updateScroll);
    return () => cancelAnimationFrame(rafRef.current);
  }, [updateScroll]);

  // Build a dotted path with slight wiggle down the left side
  // Path goes from top to bottom of viewport in SVG coords
  const pathPoints: { x: number; y: number }[] = [];
  const totalPoints = 80;
  for (let i = 0; i <= totalPoints; i++) {
    const t = i / totalPoints;
    const y = t * 100;
    // Gentle sine wiggle
    const x = 3.5 + Math.sin(t * Math.PI * 5) * 1.2 + Math.sin(t * Math.PI * 2.3) * 0.6;
    pathPoints.push({ x, y });
  }

  const pathD = pathPoints
    .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
    .join(' ');

  if (!isVisible) return null;

  return (
    <div
      className="fixed left-0 top-0 pointer-events-none"
      style={{
        width: '80px',
        height: '100vh',
        zIndex: 15,
        opacity: isVisible ? 0.25 : 0,
        transition: 'opacity 0.6s ease',
      }}
    >
      <svg
        width="80"
        height="100%"
        viewBox="0 0 8 100"
        preserveAspectRatio="none"
        className="w-full h-full"
      >
        <defs>
          <filter id="routeGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="0.08" />
          </filter>
        </defs>

        {/* Very subtle outer glow line */}
        <path
          d={pathD}
          fill="none"
          stroke="rgba(180, 150, 80, 0.03)"
          strokeWidth="0.4"
          strokeLinecap="round"
          filter="url(#routeGlow)"
        />

        {/* Main dotted route line - very subtle muted gold */}
        <path
          d={pathD}
          fill="none"
          stroke="rgba(180, 150, 80, 0.12)"
          strokeWidth="0.12"
          strokeLinecap="round"
          strokeDasharray="0.6 0.8"
        />

        {/* Second dotted line for subtle thickness */}
        <path
          d={pathD}
          fill="none"
          stroke="rgba(180, 150, 80, 0.06)"
          strokeWidth="0.2"
          strokeLinecap="round"
          strokeDasharray="0.4 1.0"
        />
      </svg>
    </div>
  );
};

export default ScrollingTradeRoutes;
